//@flow

import type { Node } from "react";
import React from "react";

export type PromiseStateType = "idle" | "loading" | "error" | "success";

export class PromiseState<T> {
    +promise: Promise<T> | null;
    +type: PromiseStateType;

    constructor(promise: Promise<T> | null, type: PromiseStateType) {
        this.promise = promise;
        this.type = type;
    }
}

/**
 * PromiseState class that indicates that there is currently no promise to observe.
 */
export class PromiseIdle<T> extends PromiseState<T> {
    constructor() {
        super(null, "idle");
    }
}

/**
 * PromiseState class that indicates that the promise has not been fulfilled (or failed) yet.
 */
export class PromiseLoading<T> extends PromiseState<T> {
    constructor(promise: Promise<T>) {
        super(promise, "loading");
    }
}

/**
 * PromiseState class that indicates that the promise has fulfilled (success)
 */
export class PromiseSuccess<T> extends PromiseState<T> {
    +value: T;
    constructor(promise: Promise<T>, value: T) {
        super(promise, "success");
        this.value = value;
    }
}

/**
 * PromiseState class that indicates that the promise has fulfilled (failed)
 */
export class PromiseError<T> extends PromiseState<T> {
    +error: Error;
    constructor(promise: Promise<T>, error: Error) {
        super(promise, "error");
        this.error = error;
    }
}

type Props<T> = {
    /**
     * The promise to observe.
     */
    promise: Promise<T> | null,
    children: (value: PromiseState<T>) => Node,
};

type State<T> = {
    value: PromiseState<T>,
};

/**
 * Watches the promise passed by props and re-renders it's children every time the state changes.
 */
class PromiseWatcher<T> extends React.Component<Props<T>, State<T>> {
    constructor(props: Props<T>) {
        super(props);
        this.state = {
            value: new PromiseIdle<T>(),
        };
    }

    componentDidUpdate(prevProps: Props<T>): any {
        const { promise } = this.props;
        // If the promise prop didn't change, do nothing
        if (prevProps.promise === promise) {
            return;
        }

        // If the promise prop changed and is currently not an instance of Promise, set the state to idle
        if (!(promise instanceof Promise)) {
            return this.setState(state => ({
                ...state,
                value: new PromiseIdle(),
            }));
        }

        // If the prop is an instance of Promise, set the state to loading...
        this.setState(state => ({
            ...state,
            value: new PromiseLoading(promise),
        }));

        // ... and wait until it succeeds or fails
        promise
            .then(value => {
                this.setState(state => ({
                    ...state,
                    value: new PromiseSuccess(promise, value),
                }));
            })
            .catch(error => {
                this.setState(state => ({
                    ...state,
                    value: new PromiseError(promise, error),
                }));
            });
    }

    render(): any {
        // Pass the current promise state to the children.
        return this.props.children(this.state.value);
    }
}

export default PromiseWatcher;
